import React, { useState, useCallback } from 'react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useAdminEntries } from '../hooks/useAdminEntries';
import { adminLogout } from '../api/admin.api';
import type { DiaryEntry, CreateDiaryEntryRequest } from '../types/admin.types';

type AdminDashboardPageProps = {
  dark: boolean;
  onLogout: () => void;
  onBack: () => void;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatDateForInput = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
};

const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

type EntryFormData = {
  content: string;
  entry_date: string;
};

export const AdminDashboardPage = ({
  dark,
  onLogout,
  onBack,
}: AdminDashboardPageProps): React.JSX.Element => {
  const { user, logout } = useAdminAuth();
  const { entries, isLoading, error, create, update, remove, isSubmitting } = useAdminEntries();

  const [editingEntry, setEditingEntry] = useState<DiaryEntry | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<EntryFormData>({
    content: '',
    entry_date: getTodayDate(),
  });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setFormData({ content: '', entry_date: getTodayDate() });
    setEditingEntry(null);
    setIsCreating(false);
  }, []);

  const handleLogout = useCallback(() => {
    void (async () => {
      try {
        await adminLogout();
      } catch {
        // Ignore logout errors
      } finally {
        logout();
        onLogout();
      }
    })();
  }, [logout, onLogout]);

  const handleCreate = useCallback(() => {
    setIsCreating(true);
    setEditingEntry(null);
    setFormData({ content: '', entry_date: getTodayDate() });
  }, []);

  const handleEdit = useCallback((entry: DiaryEntry) => {
    setEditingEntry(entry);
    setIsCreating(false);
    setFormData({
      content: entry.content,
      entry_date: formatDateForInput(entry.entry_date),
    });
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      void (async () => {
        try {
          if (editingEntry) {
            await update(editingEntry.id, formData);
          } else {
            await create(formData as CreateDiaryEntryRequest);
          }
          resetForm();
        } catch {
          // Error is handled in the hook
        }
      })();
    },
    [editingEntry, formData, create, update, resetForm]
  );

  const handleDeleteConfirm = useCallback(
    (id: string) => {
      void (async () => {
        try {
          await remove(id);
          setDeleteConfirm(null);
        } catch {
          // Error is handled in the hook
        }
      })();
    },
    [remove]
  );

  const cardClass = dark
    ? 'bg-gray-900/90 border border-gray-800'
    : 'bg-white/90 border border-gray-200';

  const inputClass = dark
    ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
    : 'bg-white border-gray-300 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500';

  return (
    <div
      className={`fixed inset-0 overflow-auto transition-colors duration-500 ${
        dark
          ? 'bg-gradient-to-br from-gray-950 via-indigo-950 to-gray-950'
          : 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50'
      }`}
    >
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
              dark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
            } shadow-lg`}
          >
            ←
          </button>

          <h1 className={`text-xl font-bold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
            Panel Admina
          </h1>

          <div className="flex items-center gap-3">
            <span className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              className={`px-4 py-2 rounded-lg text-sm transition-all hover:scale-105 ${
                dark
                  ? 'bg-red-900/50 text-red-300 hover:bg-red-900/70'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              Wyloguj
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-8">
        {/* Error message */}
        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Create/Edit Form */}
        {(isCreating || editingEntry) && (
          <div className={`mb-6 p-6 rounded-2xl shadow-lg ${cardClass}`}>
            <h2 className={`text-lg font-semibold mb-4 ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
              {editingEntry ? 'Edytuj wpis' : 'Nowy wpis'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="entry_date"
                  className={`block text-sm font-medium mb-2 ${
                    dark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Data wpisu
                </label>
                <input
                  id="entry_date"
                  type="date"
                  value={formData.entry_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, entry_date: e.target.value }))}
                  required
                  className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 ${inputClass}`}
                />
              </div>

              <div>
                <label
                  htmlFor="content"
                  className={`block text-sm font-medium mb-2 ${
                    dark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Treść
                </label>
                <textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
                  required
                  rows={8}
                  className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 resize-none ${inputClass}`}
                  placeholder="Napisz coś pięknego..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    isSubmitting
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-[1.02] active:scale-[0.98]'
                  } ${
                    dark
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                  }`}
                >
                  {isSubmitting ? 'Zapisywanie...' : editingEntry ? 'Zapisz zmiany' : 'Dodaj wpis'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className={`px-6 py-3 rounded-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] ${
                    dark
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Add button */}
        {!isCreating && !editingEntry && (
          <button
            onClick={handleCreate}
            className={`w-full mb-6 py-4 rounded-2xl font-medium transition-all hover:scale-[1.01] active:scale-[0.99] shadow-lg ${
              dark
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
            }`}
          >
            + Dodaj nowy wpis
          </button>
        )}

        {/* Entries list */}
        <div className="space-y-4">
          {isLoading ? (
            <div className={`text-center py-12 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              <div className="text-4xl mb-4">📖</div>
              <p>Ładowanie wpisów...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className={`text-center py-12 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              <div className="text-4xl mb-4">📝</div>
              <p>Brak wpisów</p>
              <p className="text-sm mt-2">Dodaj pierwszy wpis do pamiętniczka!</p>
            </div>
          ) : (
            entries.map((entry) => (
              <div key={entry.id} className={`p-6 rounded-2xl shadow-lg ${cardClass}`}>
                {deleteConfirm === entry.id ? (
                  <div className="text-center">
                    <p className={`mb-4 ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Czy na pewno chcesz usunąć ten wpis?
                    </p>
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => handleDeleteConfirm(entry.id)}
                        disabled={isSubmitting}
                        className="px-6 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                      >
                        {isSubmitting ? 'Usuwanie...' : 'Tak, usuń'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className={`px-6 py-2 rounded-lg transition-colors ${
                          dark
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                      >
                        Anuluj
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-3">
                      <span
                        className={`text-sm font-medium ${
                          dark ? 'text-indigo-400' : 'text-indigo-600'
                        }`}
                      >
                        {formatDate(entry.entry_date)}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(entry)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            dark
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(entry.id)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            dark
                              ? 'bg-red-900/50 hover:bg-red-900/70 text-red-300'
                              : 'bg-red-100 hover:bg-red-200 text-red-700'
                          }`}
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                    <p
                      className={`whitespace-pre-wrap ${dark ? 'text-gray-300' : 'text-gray-700'}`}
                      style={{ lineHeight: '1.8' }}
                    >
                      {entry.content}
                    </p>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
